import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import TrainingShell from '../../components/capacitacion/TrainingShell';
import QuestionCard from '../../components/capacitacion/QuestionCard';
import { trainingApi, StartResponse } from '../../services/trainingApi';

type Respuesta = 'a' | 'b' | 'c' | 'd';

export default function TrainingQuiz() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [start, setStart] = useState<StartResponse | null>(null);
  const [answers, setAnswers] = useState<Record<number, Respuesta>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attemptId) return;
    const raw = sessionStorage.getItem(`training-start-${attemptId}`);
    if (!raw) {
      setError('No se encontró el intento activo. Vuelve a iniciarlo desde el módulo.');
      return;
    }
    try {
      setStart(JSON.parse(raw));
    } catch {
      setError('Intento corrupto. Vuelve a iniciarlo.');
    }
  }, [attemptId]);

  const submit = useMutation({
    mutationFn: (body: { preguntaId: number; respuesta: Respuesta }[]) =>
      trainingApi.submitAttempt(Number(attemptId), body),
    onSuccess: (res) => {
      sessionStorage.setItem(
        `training-result-${res.attemptId}`,
        JSON.stringify(res)
      );
      sessionStorage.removeItem(`training-start-${attemptId}`);
      navigate(`/capacitacion/resultado/${res.attemptId}`);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || err?.message || 'Error al enviar respuestas');
    },
  });

  if (error) {
    return (
      <TrainingShell>
        <div className="max-w-xl mx-auto mt-10 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-4">
          {error}
          <button
            onClick={() => navigate('/capacitacion/modulos')}
            className="block mt-4 text-sm text-ngs-cyan hover:underline"
          >
            Volver a módulos
          </button>
        </div>
      </TrainingShell>
    );
  }

  if (!start) {
    return (
      <TrainingShell>
        <p className="text-gray-400">Cargando prueba...</p>
      </TrainingShell>
    );
  }

  const totalRespondidas = Object.keys(answers).length;
  const todasRespondidas = totalRespondidas === start.preguntas.length;

  return (
    <TrainingShell>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{start.modulo.nombre}</h1>
        <p className="text-sm text-gray-400 mt-1">
          Responde las {start.preguntas.length} preguntas. Umbral de aprobación:{' '}
          <b className="text-white">{start.modulo.umbral_aprobacion}%</b>.
        </p>
        <div className="mt-3 h-1.5 w-full bg-ngs-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-ngs-cyan transition-all"
            style={{ width: `${(totalRespondidas / start.preguntas.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {start.preguntas.map((p, idx) => (
          <QuestionCard
            key={p.id}
            index={idx}
            total={start.preguntas.length}
            pregunta={p}
            seleccion={answers[p.id]}
            onSelect={(opt) => setAnswers({ ...answers, [p.id]: opt })}
            disabled={submit.isLoading}
          />
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <span className="text-sm text-gray-400">
          {totalRespondidas} / {start.preguntas.length} respondidas
        </span>
        <button
          onClick={() =>
            submit.mutate(
              start.preguntas.map((p) => ({ preguntaId: p.id, respuesta: answers[p.id]! }))
            )
          }
          disabled={!todasRespondidas || submit.isLoading}
          className="rounded-md bg-ngs-orange text-ngs-bg font-semibold px-6 py-2.5 hover:bg-ngs-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submit.isLoading ? 'Enviando...' : 'Finalizar y ver resultado'}
        </button>
      </div>
    </TrainingShell>
  );
}
